import { useState } from "react";
import { Send, CheckCircle2, Phone, User, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  en: {
    heading: "Get Expert Help With Your Launch",
    sub: "Share your details and BrandBorn's onboarding team will call you back — 1,000+ brands launched, 98% success ratio.",
    name: "Your name", phone: "Phone number", email: "Email (optional)",
    message: "Tell us about your brand (optional)",
    btn: "Request a Callback", sending: "Sending…",
    successTitle: "Thank you! Enquiry received.",
    successSub: "Our team will contact you shortly. You can also call us directly at +91-9464645491.",
    errName: "Please enter your name", errPhone: "Please enter a valid 10-digit phone number", errFail: "Something went wrong. Please try again or call +91-9464645491.",
  },
  hi: {
    heading: "अपने लॉन्च के लिए एक्सपर्ट मदद पाएं",
    sub: "अपनी जानकारी भेजें — BrandBorn की टीम आपको कॉल करेगी। 1,000+ ब्रांड्स लॉन्च, 98% सक्सेस रेशियो।",
    name: "आपका नाम", phone: "फ़ोन नंबर", email: "ईमेल (वैकल्पिक)",
    message: "अपने ब्रांड के बारे में बताएं (वैकल्पिक)",
    btn: "कॉलबैक पाएं", sending: "भेजा जा रहा है…",
    successTitle: "धन्यवाद! आपकी जानकारी मिल गई है।",
    successSub: "हमारी टीम जल्द ही आपसे संपर्क करेगी। आप सीधे +91-9464645491 पर भी कॉल कर सकते हैं।",
    errName: "कृपया अपना नाम लिखें", errPhone: "कृपया सही 10 अंकों का फ़ोन नंबर लिखें", errFail: "कुछ गड़बड़ हुई। दोबारा कोशिश करें या +91-9464645491 पर कॉल करें।",
  },
};

export const GuideLeadForm = ({ platform, city, slug, lang = "en" }) => {
  const t = T[lang] || T.en;
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) { setError(t.errName); return; }
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10) { setError(t.errPhone); return; }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim() || "not-provided@guide-lead.in",
          phone: form.phone.trim(),
          platform: platform || "Not specified",
          message: `${form.message.trim() ? form.message.trim() + " | " : ""}Guide enquiry: ${slug}${city ? ` (${city})` : ""}`,
          source: "seo-guide",
        }),
      });
      if (!res.ok) throw new Error("fail");
      setStatus("done");
    } catch {
      setStatus("idle");
      setError(t.errFail);
    }
  };

  if (status === "done") {
    return (
      <div className="mt-12 p-8 bg-green-50 border border-green-200 rounded-2xl text-center" data-testid="guide-lead-success">
        <CheckCircle2 className="mx-auto text-green-600 mb-3" size={40} />
        <h3 className="text-xl font-bold text-gray-900 mb-2">{t.successTitle}</h3>
        <p className="text-gray-700">{t.successSub}</p>
      </div>
    );
  }

  return (
    <div className="mt-12 p-6 md:p-8 bg-gray-900 rounded-2xl" data-testid="guide-lead-form">
      <h3 className="text-2xl font-bold text-white mb-2">{t.heading}</h3>
      <p className="text-gray-300 mb-6 text-sm">{t.sub}</p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="guide-lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.name}
            className="w-full pl-9 pr-3 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-red-400 focus:outline-none" />
        </div>
        <div className="relative">
          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="guide-lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.phone}
            className="w-full pl-9 pr-3 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-red-400 focus:outline-none" />
        </div>
        <div className="relative md:col-span-2">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="guide-lead-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.email}
            className="w-full pl-9 pr-3 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-red-400 focus:outline-none" />
        </div>
        <textarea data-testid="guide-lead-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.message} rows={3}
          className="md:col-span-2 w-full px-3 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-red-400 focus:outline-none" />
        {error && <p className="md:col-span-2 text-red-400 text-sm" data-testid="guide-lead-error">{error}</p>}
        <button type="submit" disabled={status === "sending"} data-testid="guide-lead-submit"
          className="md:col-span-2 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition">
          <Send size={18} /> {status === "sending" ? t.sending : t.btn}
        </button>
      </form>
    </div>
  );
};

export default GuideLeadForm;
